import { useEstabsContext } from '../../contexts/EstabsContext';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SalesFilters, getPeriodRange } from './helpers';
import { VendasService } from './Service';

const service = new VendasService();

const PAGE_SIZE = 50;

const emptyMeta = { total: 0, totalAmount: 0 };

interface VendasContextProps {
    filters: SalesFilters;
    setFilters: React.Dispatch<React.SetStateAction<SalesFilters>>;
    vendas: any[];
    vendasLoading: boolean;
    vendasError: boolean;
    vendasMeta: { total: number; totalAmount: number };
    loadingMore: boolean;
    reloadVendas: () => Promise<void>;
    loadMoreVendas: () => Promise<void>;
    recorrentes: any[];
    recorrentesLoading: boolean;
    recorrentesError: boolean;
    reloadRecorrentes: () => Promise<void>;
}

const VendasContext = createContext<VendasContextProps | undefined>(undefined);

export const useVendasContext = () => {
    const context = useContext(VendasContext);
    if (!context) {
        throw new Error('useVendasContext must be used within a VendasProvider');
    }
    return context;
};

export const VendasProvider = ({ children }: { children: ReactNode }) => {
    const { estabSession } = useEstabsContext();
    const [filters, setFilters] = useState<SalesFilters>({
        period: 'month',
        customStart: '',
        customEnd: '',
        clientId: '',
    });

    const [vendas, setVendas] = useState<any[]>([]);
    const [vendasLoading, setVendasLoading] = useState(true);
    const [vendasError, setVendasError] = useState(false);
    const [vendasMeta, setVendasMeta] = useState(emptyMeta);
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    // Filtro trocado com outra busca em andamento: so a resposta mais recente vale.
    const lastRequest = useRef(0);

    const [recorrentes, setRecorrentes] = useState<any[]>([]);
    const [recorrentesLoading, setRecorrentesLoading] = useState(true);
    const [recorrentesError, setRecorrentesError] = useState(false);

    const fetchVendas = useCallback(
        (nextPage: number) =>
            service
                .findAll({
                    ...getPeriodRange(filters, estabSession?.timezone),
                    clientId: filters.clientId || undefined,
                    page: nextPage,
                    limit: PAGE_SIZE,
                })
                .then((response) => response?.data || {}),
        [filters, estabSession?.timezone]
    );

    const reloadVendas = useCallback(async () => {
        const request = ++lastRequest.current;
        setVendasLoading(true);
        setVendasError(false);
        try {
            const data = await fetchVendas(1);
            if (request !== lastRequest.current) return;
            setVendas(data?.data || []);
            setVendasMeta({ total: data?.total ?? 0, totalAmount: data?.totalAmount ?? 0 });
            setPage(1);
        } catch (error) {
            console.error('Erro ao carregar vendas', error);
            if (request !== lastRequest.current) return;
            // A lista anterior pode ser de outro filtro: nao fica na tela como se fosse deste.
            setVendas([]);
            setVendasMeta(emptyMeta);
            setVendasError(true);
        } finally {
            if (request === lastRequest.current) setVendasLoading(false);
        }
    }, [fetchVendas]);

    const loadMoreVendas = useCallback(async () => {
        const request = lastRequest.current;
        setLoadingMore(true);
        try {
            const data = await fetchVendas(page + 1);
            if (request !== lastRequest.current) return;
            setVendas((current) => {
                const known = new Set(current.map((venda) => venda._id));
                return [...current, ...(data?.data || []).filter((venda: any) => !known.has(venda._id))];
            });
            setPage(page + 1);
        } finally {
            setLoadingMore(false);
        }
    }, [fetchVendas, page]);

    // Recorrencias sao poucas por estabelecimento: carrega todas para a busca e as contagens valerem.
    const reloadRecorrentes = useCallback(async () => {
        setRecorrentesLoading(true);
        setRecorrentesError(false);
        try {
            const all: any[] = [];
            let nextPage = 1;
            let total = 0;

            do {
                const { data } = await service.findRecorrentes({ page: nextPage, limit: PAGE_SIZE });
                const rows = data?.data || [];
                if (!rows.length) break;
                all.push(...rows);
                total = data?.total ?? all.length;
                nextPage += 1;
            } while (all.length < total);

            setRecorrentes(all);
        } catch (error) {
            console.error('Erro ao carregar vendas recorrentes', error);
            setRecorrentesError(true);
        } finally {
            setRecorrentesLoading(false);
        }
    }, []);

    useEffect(() => {
        void reloadVendas();
    }, [reloadVendas]);

    useEffect(() => {
        void reloadRecorrentes();
    }, [reloadRecorrentes]);

    return (
        <VendasContext.Provider
            value={{
                filters,
                setFilters,
                vendas,
                vendasLoading,
                vendasError,
                vendasMeta,
                loadingMore,
                reloadVendas,
                loadMoreVendas,
                recorrentes,
                recorrentesLoading,
                recorrentesError,
                reloadRecorrentes,
            }}
        >
            {children}
        </VendasContext.Provider>
    );
};
